import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert } from "@/integrations/supabase/types";
import { toast } from "sonner";

export type Account = Tables<"accounts">;
export type AccountInsert = TablesInsert<"accounts">;

export function useAccounts() {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Account[];
    },
  });
}

export function useAccountsWithStats() {
  return useQuery({
    queryKey: ["accounts-with-stats"],
    queryFn: async () => {
      const { data: accounts, error: accountsError } = await supabase
        .from("accounts")
        .select("*")
        .order("created_at", { ascending: false });

      if (accountsError) throw accountsError;

      // Get contacts count per account
      const { data: contacts, error: contactsError } = await supabase
        .from("contacts")
        .select("account_id");

      if (contactsError) throw contactsError;

      // Get deals per account
      const { data: deals, error: dealsError } = await supabase
        .from("deals")
        .select("account_id, amount");

      if (dealsError) throw dealsError;

      return accounts.map((account) => {
        const accountContacts = contacts.filter((c) => c.account_id === account.id);
        const accountDeals = deals.filter((d) => d.account_id === account.id);
        const totalValue = accountDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

        return {
          ...account,
          contacts_count: accountContacts.length,
          deals_count: accountDeals.length,
          total_value: totalValue,
        };
      });
    },
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (account: Omit<AccountInsert, "tenant_id">) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        throw new Error("No tenant assigned. Please contact support.");
      }

      const { data, error } = await supabase
        .from("accounts")
        .insert({ ...account, tenant_id: profile.tenant_id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["accounts-with-stats"] });
      toast.success("Account created successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Account> & { id: string }) => {
      const { data, error } = await supabase
        .from("accounts")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["accounts-with-stats"] });
      toast.success("Account updated successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("accounts")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["accounts-with-stats"] });
      toast.success("Account deleted successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
